import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, BookmarkPlus, Eye, Download, MessageCircle, Star, Bot, User, Archive} from 'lucide-react';

const Explore = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // API state
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Changed from 3 to 5
  const [sortBy, setSortBy] = useState('newest');
  
  // Search input state
  const [searchInput, setSearchInput] = useState('');

  const currentQuery = searchParams.get('q') || '';
  // Fetch submissions from API
  const fetchSubmissions = useCallback(async (page = 1, limit = itemsPerPage, sort = 'newest') => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all submissions to determine total count
      const allResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/submissions/public?skip=0&limit=1000`);
      if (!allResponse.ok) {
        throw new Error(`HTTP error! status: ${allResponse.status}`);
      }
      const allData = await allResponse.json();
      
      // Filter to get only the latest version of each submission
      let latestVersions = getLatestVersions(allData);
      
      // Apply sorting to all data before pagination
      latestVersions = sortSubmissions(latestVersions, sort);
      
      const totalCount = latestVersions.length;
      setTotalResults(totalCount);
      
      // Apply pagination
      const skip = (page - 1) * limit;
      const paginatedData = latestVersions.slice(skip, skip + limit);
      
      // Transform API data to match expected format
      const transformedData = paginatedData.map(submission => ({
        id: submission.aixiv_id || submission.id,
        title: submission.title,
        authors: submission.authors || [],
        authorshipType: submission.authorship_type || 'human',
        abstract: submission.abstract || 'No abstract available',
        type: submission.doc_type || 'paper',
        status: submission.status || 'published',
        category: Array.isArray(submission.category) ? submission.category.join(', ') : submission.category || 'General',
        date: new Date(submission.created_at).toLocaleDateString(),
        createdAt: submission.created_at, // Keep for reference
        metrics: {
          views: submission.views || 0,
          downloads: submission.downloads || 0,
          comments: submission.comments || 0,
          citations: submission.citations || 0
        },        
        isAI: submission.authorship_type === 'ai',
        badges: getBadges(submission),
        correspondingAuthor: submission.corresponding_author,
        keywords: submission.keywords || [],
        s3_url: submission.s3_url,
        version: submission.version || '1.0',
        aixiv_id: submission.aixiv_id,
        doi: submission.doi
      }));
      
      setSubmissions(transformedData);
      
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submissions. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);

  // Helper function to get only the latest version of each submission
  const getLatestVersions = (submissions) => {
    const submissionMap = new Map();
    
    // Group submissions by aixiv_id and keep the latest version
    submissions.forEach(submission => {
      const aixivId = submission.aixiv_id;
      if (!aixivId) return; // Skip submissions without aixiv_id
      
      if (!submissionMap.has(aixivId)) {
        submissionMap.set(aixivId, submission);
      } else {
        const existing = submissionMap.get(aixivId);
        // Compare versions (assuming format like "1.0", "1.1", etc.)
        const currentVersion = parseFloat(submission.version || '1.0');
        const existingVersion = parseFloat(existing.version || '1.0');
        
        if (currentVersion > existingVersion) {
          submissionMap.set(aixivId, submission);
        }
      }
    });
    
    return Array.from(submissionMap.values());
  };

  // Helper function to generate badges based on submission data
  const getBadges = (submission) => {
    const badges = [];
    if (submission.status?.toLowerCase() === 'published') {
      badges.push('peer-reviewed');
    }
    if (submission.doc_type === 'proposal') {
      badges.push('research-proposal');
    }
    return badges;
  };
  // Sort submissions based on selected criteria
  const sortSubmissions = (submissions, sortBy) => {
    const sorted = [...submissions];
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'most-viewed':
        return sorted.sort((a, b) => (b.views || 0) - (a.views || 0));
      case 'most-downloaded':
        return sorted.sort((a, b) => (b.downloads || 0) - (a.downloads || 0));
      case 'most-discussed':
        return sorted.sort((a, b) => (b.comments || 0) - (a.comments || 0));
      case 'most-cited':
        return sorted.sort((a, b) => (b.citations || 0) - (a.citations || 0));
      default:
        return sorted;
    }
  };


  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchSubmissions(newPage, itemsPerPage, sortBy);
    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPage.toString());
    setSearchParams(newSearchParams);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle sort change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1); // Reset to first page when sorting changes
    fetchSubmissions(1, itemsPerPage, newSort);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', '1');
    newSearchParams.set('sort', newSort);
    setSearchParams(newSearchParams);
  };  // Handle search - triggered by button click or Enter key
  const handleSearch = () => {
    const query = searchInput.trim();
    const newSearchParams = new URLSearchParams(searchParams);
    if (query) {
      newSearchParams.set('q', query);
    } else {
      newSearchParams.delete('q');
    }
    newSearchParams.set('page', '1'); // Reset to first page
    setSearchParams(newSearchParams);
    setCurrentPage(1);
    // Re-fetch data with search query
    fetchSubmissionsWithSearch(query, 1, itemsPerPage, sortBy);
  };

  // Handle Enter key in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Fetch submissions with search filter
  const fetchSubmissionsWithSearch = useCallback(async (searchQuery, page, limit, sort) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all submissions
      const allResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/submissions/public?skip=0&limit=1000`);
      if (!allResponse.ok) {
        throw new Error(`HTTP error! status: ${allResponse.status}`);
      }
      const allData = await allResponse.json();
      
      // Filter to get only the latest version of each submission
      let latestVersions = getLatestVersions(allData);
      
      // Apply search filter if query exists
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        latestVersions = latestVersions.filter(submission => {
          const title = (submission.title || '').toLowerCase();
          const abstract = (submission.abstract || '').toLowerCase();
          const authors = Array.isArray(submission.authors) 
            ? submission.authors.join(' ').toLowerCase() 
            : '';
          const correspondingAuthor = (submission.corresponding_author || '').toLowerCase();
          
          return title.includes(query) || 
                 abstract.includes(query) || 
                 authors.includes(query) ||
                 correspondingAuthor.includes(query);
        });
      }
      
      // Apply sorting
      latestVersions = sortSubmissions(latestVersions, sort);
      
      const totalCount = latestVersions.length;
      setTotalResults(totalCount);
      
      // Apply pagination
      const skip = (page - 1) * limit;
      const paginatedData = latestVersions.slice(skip, skip + limit);
      
      // Transform API data to match expected format
      const transformedData = paginatedData.map(submission => ({
        id: submission.aixiv_id || submission.id,
        title: submission.title,
        authors: submission.authors || [],
        authorshipType: submission.authorship_type || 'human',
        abstract: submission.abstract || 'No abstract available',
        type: submission.doc_type || 'paper',
        status: submission.status || 'published',
        category: Array.isArray(submission.category) ? submission.category.join(', ') : submission.category || 'General',
        date: new Date(submission.created_at).toLocaleDateString(),
        createdAt: submission.created_at,
        metrics: {
          views: submission.views || 0,
          downloads: submission.downloads || 0,
          comments: submission.comments || 0,
          citations: submission.citations || 0
        },        
        isAI: submission.authorship_type === 'ai',
        badges: getBadges(submission),
        correspondingAuthor: submission.corresponding_author,
        keywords: submission.keywords || [],
        s3_url: submission.s3_url,
        version: submission.version || '1.0',
        aixiv_id: submission.aixiv_id,
        doi: submission.doi
      }));
      
      setSubmissions(transformedData);
      
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submissions. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);
  // Load data on component mount and when search params change
  useEffect(() => {
    const page = parseInt(searchParams.get('page')) || 1;
    const sort = searchParams.get('sort') || 'newest';
    const query = searchParams.get('q') || '';
    setCurrentPage(page);
    setSortBy(sort);
    setSearchInput(query); // Initialize search input with URL query
    
    if (query) {
      fetchSubmissionsWithSearch(query, page, itemsPerPage, sort);
    } else {
      fetchSubmissions(page, itemsPerPage, sort);
    }
  }, [searchParams, fetchSubmissions, fetchSubmissionsWithSearch, itemsPerPage]);
  // Calculate pagination info
  const totalPages = Math.ceil(totalResults / itemsPerPage);

  const renderResultCard = (result) => (
    <div key={result.id} className="card p-6 hover:shadow-lg transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-2">          
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
            result.type === 'proposal' 
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
          }`}>
            {result.type.toUpperCase()}
          </span>
          
          {/* Authorship Type Badge */}
          {result.authorshipType === 'ai' ? (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
              <Bot className="h-3 w-3 mr-1" />
              AI
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
              <User className="h-3 w-3 mr-1" />
              Human
            </span>
          )}
          
          {result.badges.map((badge) => (
            <span key={badge} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
              {badge}
            </span>
          ))}
        </div>

        <div className="flex items-center space-x-2">
          <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <BookmarkPlus className="h-4 w-4" />
          </button>
          <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <Star className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <Link to={`/abs/${result.id}`}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 hover:text-primary-600 cursor-pointer">
        {result.title}
      </h3>
      </Link>
      
      <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
        {result.authors.map((author, index) => (
          <span key={index} className="hover:text-primary-600 cursor-pointer">
            {author}{index < result.authors.length - 1 ? ', ' : ''}
          </span>
        ))}        {result.correspondingAuthor && (
          <span className="text-gray-500 dark:text-gray-400">
            {' • '}{result.authorshipType === 'ai' ? 'Corresponding Human' : 'Corresponding Author'}: {result.correspondingAuthor}
          </span>
        )}
      </p>
      
      <p className="text-gray-700 dark:text-gray-300 text-sm mb-4 line-clamp-3">
        {result.abstract}
      </p>

      {/* Metadata */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
        <span>{result.category} • {result.date}</span>
        <div className="flex items-center space-x-2">
        <span className={`px-2 py-1 rounded text-xs ${
             result.status?.toLowerCase() === 'published' 
            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
        }`}>
          {result.status}
        </span>
           {result.aixiv_id && (
             <span className="text-xs text-gray-500">
               {result.aixiv_id} v{result.version}
             </span>
           )}
        </div>
      </div>

      {/* Metrics & Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
          <span className="flex items-center">
            <Eye className="h-4 w-4 mr-1" />
            {result.metrics.views.toLocaleString()}
          </span>
          <span className="flex items-center">
            <Download className="h-4 w-4 mr-1" />
            {result.metrics.downloads.toLocaleString()}
          </span>
          <span className="flex items-center">
            <MessageCircle className="h-4 w-4 mr-1" />
            {result.metrics.comments}
          </span>
          <span className="flex items-center">
            <Star className="h-4 w-4 mr-1" />
            {result.metrics.citations}
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <Link to={`/abs/${result.id}`} className="btn-primary text-xs py-1 px-3">
            View
          </Link>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading submissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button 
            onClick={() => fetchSubmissions(currentPage, itemsPerPage, sortBy)}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Explore</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Discover papers, proposals, and research insights
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <a
              href="https://aixiv.science/list"
              className="btn-secondary flex items-center space-x-2"
              title="View complete paper archive organized by month"
            >
              <Archive className="h-4 w-4" />
              <span>Archive</span>
            </a>
          </div>
        </div>        {/* Search Bar */}
        <div className="mb-6">
          <div className="flex items-center space-x-2">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search papers, proposals, authors..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="input-field pl-10 w-full"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            <button 
              onClick={handleSearch}
              className="btn-primary px-6 py-2 whitespace-nowrap"
            >
              Search
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600 dark:text-gray-400">
              Found {totalResults} results
              {currentQuery && ` for "${currentQuery}"`}
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </p>
            
            <select 
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="input-field w-48"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="most-viewed">Most Viewed</option>
              <option value="most-downloaded">Most Downloaded</option>
              <option value="most-discussed">Most Discussed</option>
              <option value="most-cited">Most Cited</option>
            </select>
          </div>

          {submissions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-lg">No submissions found</p>
              <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                Try adjusting your search terms
              </p>
            </div>
          ) : (
          <div className="space-y-6">
              {submissions.map(renderResultCard)}
          </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <div className="flex items-center space-x-2">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = index + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = index + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + index;
                  } else {
                    pageNumber = currentPage - 2 + index;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`px-3 py-2 rounded ${
                        pageNumber === currentPage
                          ? 'bg-primary-600 text-white'
                          : 'btn-secondary'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Explore;
